import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy, User, Search } from "lucide-react";
import Header from "@/components/header";
import { useTranslation } from "@/i18n/LanguageContext";
import { useTitle } from "@/hooks/use-title";
import { Link } from "wouter";

interface RankedUser {
  id: number;
  displayName: string;
  cacatais: number;
  avatarUrl: string | null;
}

const formatLargeNumber = (numStr: string) => {
  const num = BigInt(numStr);
  const trillion = BigInt(1e12);
  const billion = BigInt(1e9);
  const million = BigInt(1e6);

  if (num >= trillion) {
    return `${Number(num / trillion).toLocaleString()} trillion`;
  } else if (num >= billion) {
    return `${Number(num / billion).toLocaleString()} billion`;
  } else if (num >= million) {
    return `${Number(num / million).toLocaleString()} million`;
  }
  return Number(num).toLocaleString();
};

const formatNumber = new Intl.NumberFormat().format;

export default function RankPage() {
  useTitle("Top Miners");
  const { t } = useTranslation();
  const { data: users } = useQuery<RankedUser[]>({
    queryKey: ["/api/users/ranking"],
  });

  const { data: keysScanned } = useQuery<{ total: string }>({
    queryKey: ["/api/users/total-keys-scanned"],
  });

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <div className="container mx-auto pt-24 px-6 space-y-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Search className="h-6 w-6 text-blue-500" />
              {t("rank.keys_scanned")}: {keysScanned ? formatLargeNumber(keysScanned.total) : '0'}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy className="h-6 w-6 text-yellow-500" />
              Top 10 Miners
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Cacatais</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.map((user, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatarUrl || undefined} />
                          <AvatarFallback>
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <Link href={`/users/${user.displayName ? user.displayName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : user.id}`} className="hover:underline hover:text-blue-400 transition-colors">
                          {user.displayName || 'Anonymous'}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatNumber(user.cacatais)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}